import React, { forwardRef, useImperativeHandle, useState, useMemo } from 'react';
import { Table, Tag, Badge } from 'antd';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { getCustomFilterDropdown, getColumnSearchProps } from './TableFilters';

const TablaSolicitudes = forwardRef(({ 
  dataSource, 
  originalDataSource,
  loading, 
  onOpenChat, 
  onTableChange 
}, ref) => {
  const [tableKey, setTableKey] = useState(0);
  
  // redux state para acceder a los últimos mensajes y al historial del chat
  const { historialActual, solicitudActiva, ultimosMensajes } = useSelector((state) => state.atencionReducer);
  
  // función para obtener el último mensaje real desde redux
  const getUltimoMensajeFromRedux = (solicitudId) => {
    // primero: verificar si tenemos el último mensaje almacenado
    if (ultimosMensajes[solicitudId]) {
      const ultimoMsg = ultimosMensajes[solicitudId];
      return {
        mensaje: ultimoMsg.comentario,
        fecha: ultimoMsg.fechaCreacion,
        usuario: ultimoMsg.usuarioNombre,
        esDeRedux: true,
        tipo: 'almacenado'
      };
    }
    
    // segundo: verificar si esta solicitud tiene historial cargado actualmente
    if (solicitudActiva === solicitudId && historialActual && historialActual.length > 0) {
      const mensajesReales = historialActual.filter(msg => !msg.isTemporary);
      if (mensajesReales.length > 0) {
        const ultimoMsg = mensajesReales[mensajesReales.length - 1];
        return {
          mensaje: ultimoMsg.comentario,
          fecha: ultimoMsg.fechaCreacion,
          usuario: ultimoMsg.usuarioNombre,
          esDeRedux: true,
          tipo: 'actual'
        };
      }
    }
    
    return null;
  };

  useImperativeHandle(ref, () => ({
    clearAllFilters: () => {
      setTableKey(prev => prev + 1);
      
      setTimeout(() => {
        if (onTableChange) {
          onTableChange({
            filteredData: dataSource,
            filters: {},
            sorter: {},
            pagination: { current: 1 }
          });
        }
      }, 100);
    }
  }), [dataSource, onTableChange]);

  // función para obtener badge de estado transparente
  const getEstadoBadge = (estatus) => {
    const config = {
      'abierto': { color: '#52c41a', text: 'Abierto' },
      'en proceso': { color: 'var(--color-naranja)', text: 'En proceso' },
      'cerrado': { color: '#8c8c8c', text: 'Cerrado' },
      'pendiente': { color: '#faad14', text: 'Pendiente' }
    };
    
    const statusConfig = config[estatus?.toLowerCase()] || { color: '#8c8c8c', text: estatus };
    
    return (
      <Tag 
        style={{ 
          margin: 0,
          background: 'transparent',
          border: `1px solid ${statusConfig.color}`,
          color: statusConfig.color,
          borderRadius: '4px',
          fontSize: '12px',
          padding: '2px 6px'
        }}
      >
        {statusConfig.text}
      </Tag>
    );
  };

  // obtener estados únicos para filtros (desde datos originales, no filtrados)
  const estadosUnicos = useMemo(() => {
    const dataForFilters = originalDataSource || dataSource;
    const estados = [...new Set(dataForFilters.map(item => item.estatus))].filter(Boolean);
    return estados.map(estado => ({ text: estado, value: estado }));
  }, [originalDataSource, dataSource]);

  // obtener proveedores únicos para filtros - diferencia clave
  const proveedoresUnicos = useMemo(() => {
    const dataForFilters = originalDataSource || dataSource;
    const proveedores = [...new Set(dataForFilters.map(item => item.proveedorNombre))].filter(Boolean);
    return proveedores.map(proveedor => ({ text: proveedor, value: proveedor }));
  }, [originalDataSource, dataSource]);

  const columns = [
    {
      title: 'ID',
      dataIndex: 'proveedorSolicitudID',
      key: 'proveedorSolicitudID',
      width: 100,
      align: 'center',
      ...getColumnSearchProps('proveedorSolicitudID', 'Buscar ID'),
      sorter: (a, b) => a.proveedorSolicitudID - b.proveedorSolicitudID,
      render: (id, record) => (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          <span style={{ color: 'var(--color-naranja)', fontWeight: '500' }}>
            #{id}
          </span>
          {record.ultimoComentarioHistorial && !record.ultimoComentarioHistorial.leido && (
            <Badge 
              dot
              size="small" 
              style={{ 
                backgroundColor: 'var(--color-naranja)',
                position: 'absolute',
                right: -15,
                top: -3
              }}
            />
          )}
        </div>
      )
    },
    {
      title: 'Usuario',
      dataIndex: 'proveedorUsuarioNombre',
      key: 'proveedorUsuarioNombre',
      width: 150,
      render: (usuario) => (
        <span style={{ fontSize: '12px', color: '#666' }}>
          {usuario}
        </span>
      )
    },
    {
      title: 'Proveedor',
      dataIndex: 'proveedorNombre',
      key: 'proveedorNombre',
      width: 180,
      ...getCustomFilterDropdown(proveedoresUnicos),
      onFilter: (value, record) => record.proveedorNombre === value,
      ellipsis: {
        showTitle: false,
      },
      render: (proveedor) => (
        <span style={{ fontSize: '12px', color: '#333', fontWeight: '500' }}>
          {proveedor}
        </span>
      )
    },
    {
      title: 'Descripción',
      dataIndex: 'comentario',
      key: 'comentario',
      width: 250,
      ...getColumnSearchProps('comentario', 'Buscar en descripción'),
      ellipsis: {
        showTitle: false,
      },
      render: (comentario) => <span>{comentario}</span>
    },
    {
      title: 'Último mensaje',
      key: 'ultimoMensaje',
      width: 250,
      ellipsis: {
        showTitle: false,
      },
      render: (_, record) => {
        // intentar obtener último mensaje real desde redux
        const mensajeRedux = getUltimoMensajeFromRedux(record.proveedorSolicitudID);
        
        // si hay datos de redux, usarlos; sino mostrar descripción original
        const ultimoMensaje = mensajeRedux ? mensajeRedux.mensaje : record.comentario;
        const fechaUltimo = mensajeRedux ? mensajeRedux.fecha : record.fechaCreacion;
        const usuario = mensajeRedux?.usuario;
        
        return (
          <div style={{ color: '#666', fontSize: '12px' }}>
            <div style={{ 
              overflow: 'hidden', 
              textOverflow: 'ellipsis', 
              whiteSpace: 'nowrap',
              marginBottom: '2px'
            }}>
              {ultimoMensaje}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: '#999',
              fontStyle: 'italic'
            }}>
              {dayjs(fechaUltimo).format('DD/MM HH:mm')}
              {usuario && (
                <span style={{ marginLeft: '6px' }}>
                  • {usuario}
                </span>
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Fecha creación',
      dataIndex: 'fechaCreacion',
      key: 'fechaCreacion',
      width: 170,
      align: 'center',
      sorter: (a, b) => dayjs(a.fechaCreacion).unix() - dayjs(b.fechaCreacion).unix(),
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY HH:mm')
    },
    {
      title: 'Estado',
      dataIndex: 'estatus',
      key: 'estatus',
      width: 120,
      align: 'center',
      ...getCustomFilterDropdown(estadosUnicos),
      onFilter: (value, record) => record.estatus === value,
      render: (estatus) => getEstadoBadge(estatus)
    }
  ];

  return (
    <Table
      key={tableKey}
      className="tabla-solicitudes"
      size="small"
      columns={columns}
      rowKey="proveedorSolicitudID"
      dataSource={dataSource}
      loading={loading}
      showSorterTooltip={false}
      showHeader={true}
      onRow={(record) => ({
        onClick: () => onOpenChat(record),
        style: { cursor: 'pointer' }
      })}
      pagination={{
        showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} solicitudes`,
        defaultCurrent: 1,
        defaultPageSize: 10,
        showSizeChanger: true,
        showQuickJumper: true,
        pageSizeOptions: ['5', '10', '20', '50'],
        locale: {
          items_per_page: '/ página',
          jump_to: 'Ir a',
          jump_to_confirm: 'Confirmar',
          page: 'Página',
          prev_page: 'Página anterior',
          next_page: 'Página siguiente',
          prev_5: '5 páginas anteriores',
          next_5: '5 páginas siguientes',
          prev_3: '3 páginas anteriores',
          next_3: '3 páginas siguientes'
        }
      }}
      scroll={{ x: 1200 }} // ajustado por columnas adicionales
      onChange={(pagination, filters, sorter, { currentDataSource }) => {
        if (onTableChange) {
          onTableChange({
            filteredData: currentDataSource,
            filters,
            sorter,
            pagination
          });
        }
      }}
      locale={{
        filterReset: 'Limpiar',
        filterConfirm: 'OK',
        emptyText: 'No hay solicitudes'
      }}
      rowClassName={(record) => 
        record.isTemporary ? 'row-temporary' : 
        (record.ultimoComentarioHistorial && !record.ultimoComentarioHistorial.leido) ? 'row-unread' : ''
      }
    />
  );
});

export default TablaSolicitudes;